/**
 * Share links for skills.
 *
 * A skill has its own page — `/agents/skills/:skillId`, optionally
 * `/versions/v:version` — so a share link is just that page's URL. Nothing has
 * to be resolved against a list, a tab, or a filter for the recipient to land
 * on the right thing: the id is in the path, and the page fetches that one
 * skill. See `Goto.createSkillRoute` for the path itself; this module covers
 * the two things the route alone doesn't — making the path absolute, and
 * reading the version segment back out of it.
 */

import type { SkillContentSeed } from "@/shared/domain/skills";
import { Env } from "@/shared/environment/Env";
import { Goto } from "@/shared/navigation";

/**
 * Pre-route link param. The agent template's "Edit skill" link minted
 * `?skillId=…&name=…&tab=…`, and links carrying it are out in chat threads and
 * docs, so the Skills list page still reads it — to redirect to that skill's
 * page. The companion `name` and `tab` params existed only to aim a filtered
 * list at the right row on the right tab, a job the skill's own page doesn't
 * have, so they are read by nobody and go away with the redirect.
 */
export const LEGACY_SKILL_ID_PARAM = "skillId";

/**
 * The skill id an older query-param link points at, or null when the query
 * string carries none.
 */
export function parseLegacySkillLink(params: URLSearchParams): string | null {
  const raw = params.get(LEGACY_SKILL_ID_PARAM)?.trim();
  return raw === undefined || raw.length === 0 ? null : raw;
}

/**
 * Read a `:version` route segment as a version number.
 *
 * Accepts the `v3` spelling the app mints and a bare `3` for anyone who typed
 * the URL by hand. Returns null for anything else — a hand-mangled segment
 * must not resolve to some *other* version's body.
 */
export function parseSkillVersionParam(raw: string | undefined): number | null {
  if (raw === undefined) {
    return null;
  }
  const digits = raw.trim().replace(/^v/i, "");
  if (!/^\d+$/.test(digits)) {
    return null;
  }
  const parsed = Number(digits);
  return parsed >= 1 ? parsed : null;
}

/**
 * Marks the skill page as being in edit mode.
 *
 * A query param rather than a route segment because it names a *mode* over the
 * same resource, not a different one. Keeping it in the URL (instead of in
 * component state) is what lets the Skills table's per-version pen icon be an
 * ordinary link into the same page every other affordance lands on.
 */
export const SKILL_EDIT_PARAM = "edit";

/** The skill page, opened straight into edit mode. */
export function skillEditPath(
  skillId: string,
  version?: number | null,
): string {
  return `${Goto.createSkillRoute(skillId, version)}?${SKILL_EDIT_PARAM}=1`;
}

/**
 * Router state the Skills list attaches when it opens {@link skillEditPath}
 * on behalf of the `client_create_skill` chat tool's update path: the AI's
 * proposed content, which the skill page overlays on the loaded version in
 * its edit form.
 *
 * Router state rather than another sessionStorage slot because its lifetime
 * is exactly right on its own: it arrives with the navigation that means
 * "edit with this proposal", survives a reload mid-review (nothing has been
 * saved yet, so re-presenting the proposal is correct), and is dropped by the
 * page's own mode toggling — Cancel discards the proposal.
 */
export interface SkillEditSeedState {
  skillContentSeed: SkillContentSeed;
}

/**
 * Read a {@link SkillContentSeed} back out of `location.state`, or null when
 * none is present.
 *
 * History state is a trust boundary of the same kind as the draft's
 * sessionStorage slot — it persists across reloads and can be minted by
 * anything with access to the History API — so the shape is re-validated
 * field by field rather than cast. Blank strings are dropped for the same
 * reason `parseSkillDraftShape` drops them: consumers overlay with `??`, and
 * `""` would blank a populated field.
 */
export function skillContentSeedFromLocationState(
  state: unknown,
): SkillContentSeed | null {
  if (typeof state !== "object" || state === null) {
    return null;
  }
  const raw = (state as Record<string, unknown>).skillContentSeed;
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const { description, body, tags, removeTags } = raw as Record<
    string,
    unknown
  >;
  const seed: SkillContentSeed = {};
  if (typeof description === "string" && description.trim() !== "") {
    seed.description = description;
  }
  if (typeof body === "string" && body.trim() !== "") {
    seed.body = body;
  }
  const added = tagListFromState(tags);
  if (added !== undefined) {
    seed.tags = added;
  }
  const removed = tagListFromState(removeTags);
  if (removed !== undefined) {
    seed.removeTags = removed;
  }
  return seed.description === undefined &&
    seed.body === undefined &&
    seed.tags === undefined &&
    seed.removeTags === undefined
    ? null
    : seed;
}

/**
 * The usable tags in a list off history state, or `undefined` when none
 * survive.
 *
 * Trimmed and blank-dropped: these become chips and then `put_tags` /
 * `remove_tags` values, where a stray `"  "` would be a real tag nobody asked
 * for. A partly-malformed list keeps the entries it does carry rather than
 * losing the whole retag to one bad element.
 */
function tagListFromState(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const usable = value
    .filter((tag): tag is string => typeof tag === "string")
    .map((tag) => tag.trim())
    .filter((tag) => tag !== "");
  return usable.length > 0 ? usable : undefined;
}

export interface SkillShareLinkParams {
  skillId: string;
  /** Omit (or pass null) to link the skill itself rather than one version. */
  version?: number | null;
}

/**
 * Fully-qualified URL of a skill's page, or of one of its versions.
 *
 * ``Env.appBasePath`` keeps branch-per-URL deploys working — building off
 * ``window.location.origin`` alone drops the React Router basename and lands
 * the recipient on a whitescreen.
 */
export function buildSkillShareLink({
  skillId,
  version,
}: SkillShareLinkParams): string {
  const url = new URL(
    `${Env.appBasePath}${Goto.createSkillRoute(skillId, version)}`,
    window.location.origin,
  );
  return url.toString();
}
